<template>
  <DocumentCenterModal
    :download="download"
    :open="open"
    :preferred-locales="preferredLocales"
    @close-modal="closeModal()"
    @after-close-modal="afterCloseModal()"
  />
</template>

<script setup lang="ts">
import DocumentCenterModal from '~/components/downloads/documents/DocumentCenterModal.vue';
import { useDownloadStore } from '~/stores/downloadStore/downloadStore';

import type {
  DownloadByIdQueryModel,
  DownloadByIdResponseModel
} from '~/server/api/downloads/byId.get';
import type { ContentfulLocaleCode } from '~/lib/ContentfulService/model';
import { DOWNLOAD_LOCALE_CONTENTFUL_LOCALE_MAP } from '~/lib/DownloadService/constants';
import { isDefined } from '~/utils/guards/isDefined';
import { ToastMessageType } from '~/composables/useShowToast';

const { $resolvedLocale, $textDictionary } = useNuxtApp();
const logger = useLogger();
const toasts = useShowToast();

const route = useRoute();
const router = useRouter();
const store = useDownloadStore();

const open = ref(false);
const downloadId = computed<string | undefined>(
  () => route.hash?.split('download=')?.[1]
);

// XXX: forward loading state?
const { data: download, error } = await useAsyncData(
  async () => {
    if (downloadId.value) {
      const cachedDownload = Object.values(store.downloads?.results ?? [])
        .map((result) => result.hits)
        .flat()
        .find((hit) => hit.id === downloadId.value);

      return (
        cachedDownload ??
        (await $fetch<DownloadByIdResponseModel>('/api/downloads/byId', {
          method: 'get',
          query: {
            id: downloadId.value,
            locale: $resolvedLocale.value!
          } satisfies DownloadByIdQueryModel
        }))
      );
    }

    return null;
  },
  {
    deep: false,
    lazy: true,
    watch: [downloadId]
  }
);

const preferredLocales = computed<ContentfulLocaleCode[] | undefined>(() =>
  store.filters['medialink.locale']
    ?.map((locale) => DOWNLOAD_LOCALE_CONTENTFUL_LOCALE_MAP[locale])
    .filter(isDefined)
);

watch(error, () => {
  if (error.value) {
    const message =
      $textDictionary.value['downloadCenter.modal.display.error'] ??
      `Failed to display download modal for download with id ${downloadId.value}`;

    toasts.showToast(message, {
      severity: ToastMessageType.ERROR
    });

    logger.error(
      `error during download fetch in document center standalone modal for id ${downloadId.value}`,
      error.value
    );
  }
});

watch(download, () => {
  // only open download if download id is given
  // download can actually be `null` (e.g. for 404 or other errors).
  if (downloadId.value) {
    open.value = true;
  }
});

watch(
  () => route.hash,
  () => {
    if (!route.hash?.split('download=')?.[1]) {
      closeModal();
    }
  }
);

function closeModal() {
  open.value = false;
}

function afterCloseModal() {
  // closeModal is not triggered while closing the modal via esc...
  // so we also have to set the open value here.
  open.value = false;

  router.push({
    hash: undefined,
    query: route.query
  });
}
</script>
